import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Users, ShieldCheck, 
  CheckCircle2, 
  Zap, Layers, Target, Activity,
  Layout, Globe, Landmark, BarChart3, Quote
} from 'lucide-react';
import styles from './Brands.module.scss';
import clsx from 'clsx';

// Images (using the ones generated)
const IMG_HUB = '/assets/images/panama_brands_strategic_hub_1778199044432.png';
const IMG_PRESENCE = '/assets/images/panama_luxury_brand_presence_1778199059097.png';
const IMG_CONTROL = '/assets/images/panama_metrics_control_lifestyle_1778199074102.png';

// ==============================
// MINI MOCKUPS (REAL UI)
// ==============================
const MiniDashboard = ({ t }) => (
  <div className={styles.uiMockup}>
    <div className={styles.mockBrowser}>
      <div className={styles.dots}><span></span><span></span><span></span></div>
      <div className={styles.url}>{t('mockups.dashboard.url')}</div>
    </div>
    <div className={styles.mockBody}>
      <aside className={styles.mockSide}>
        <div className={clsx(styles.sideItem, styles.active)}><Layout size={12}/> {t('mockups.dashboard.sidebar.dashboard')}</div>
        <div className={styles.sideItem}><Users size={12}/> {t('mockups.dashboard.sidebar.network')}</div>
        <div className={styles.sideItem}><Zap size={12}/> {t('mockups.dashboard.sidebar.strategies')}</div>
        <div className={styles.sideItem}><ShieldCheck size={12}/> {t('mockups.dashboard.sidebar.subscriptions')}</div>
      </aside>
      <main className={styles.mockContent}>
        <div className={styles.mockCard}>
          <div className={styles.cardHeader}>
            <span className={styles.name}>{t('mockups.dashboard.card.name')}</span>
            <span className={styles.status}>{t('mockups.dashboard.card.status')}</span>
          </div>
          <div className={styles.progress}><div className={styles.fill} style={{width: '70%'}}></div></div>
          <div className={styles.meta}>
            <span>{t('mockups.dashboard.card.assets', { count: 12, total: 15 })}</span>
            <span>{t('mockups.dashboard.card.secured', { amount: '4,200.00' })}</span>
          </div>
        </div>
      </main>
    </div>
  </div>
);

const MiniMetrics = ({ t }) => (
  <div className={styles.metricsUi}>
    <div className={styles.metHeader}>{t('mockups.metrics.header')}</div>
    <div className={styles.metGrid}>
      <div className={styles.metCircle}>
        <div className={styles.circleActive}></div>
        <div className={styles.circleVal}>94%</div>
        <div className={styles.circleLab}>{t('mockups.metrics.authenticity')}</div>
      </div>
      <div className={styles.metDetails}>
        <div className={styles.metLine} style={{width: '90%'}}><span>Primary Market</span></div>
        <div className={styles.metLine} style={{width: '60%'}}><span>Regional Hub</span></div>
        <div className={styles.metLine} style={{width: '40%'}}><span>Global Destination</span></div>
      </div>
    </div>
  </div>
);


const Brands = () => {
  const { t } = useTranslation('brands');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.23, 1, 0.32, 1] } }
  };

  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } }
  };

  const solutions = [
    {
      icon: <Target size={24} />,
      title: t('solutions.0.title'),
      text: t('solutions.0.text')
    },
    {
      icon: <Layers size={24} />,
      title: t('solutions.1.title'),
      text: t('solutions.1.text')
    },
    {
      icon: <Zap size={24} />,
      title: t('solutions.2.title'),
      text: t('solutions.2.text')
    },
    {
      icon: <Activity size={24} />,
      title: t('solutions.3.title'),
      text: t('solutions.3.text')
    }
  ];

  return (
    <div className={styles.brandsPage}>
      {/* 1. HERO */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <motion.div initial="hidden" animate="visible" variants={stagger} className={styles.heroInner}>
            <motion.div variants={slideUp} className={styles.sectionEyebrow}>
              {t('hero.eyebrow')}
            </motion.div>
            <motion.h1 variants={slideUp} className={styles.heroTitle}>
              {t('hero.title')}
            </motion.h1>
            <motion.p variants={slideUp} className={styles.heroSubtitle}>
              {t('hero.subtitle')}
            </motion.p>
            <motion.div variants={slideUp} className={styles.ctaBox}>
              <Link to="/register" className={styles.primaryBtn}>
                {t('hero.primary')}
              </Link>
              <Link to="/about" className={styles.secondaryBtn}>
                {t('hero.secondary')}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. STATS */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            {t('stats', { returnObjects: true }).map((stat, i) => (
              <div key={i} className={stat.value === '0' ? clsx(styles.statItem, styles.zeroStat) : styles.statItem}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. PROBLEM / SOLUTION 1 */}
      <section className={styles.problemsSection}>
        <div className={styles.container}>
          <div className={styles.problemCard}>
            <div className={styles.problemText}>
              <div className={styles.sectionEyebrow}>{t('problems.centralization.eyebrow')}</div>
              <h3>{t('problems.centralization.title')}</h3>
              <p>{t('problems.centralization.desc')}</p>
              <ul className={styles.benefitItems}>
                {t('problems.centralization.benefits', { returnObjects: true }).map((benefit, i) => (
                  <li key={i}><CheckCircle2 size={18} /> {benefit}</li>
                ))}
              </ul>
            </div>
            <div className={styles.mockWrapper}>
              <MiniDashboard t={t} />
            </div>
          </div>

          <div className={clsx(styles.problemCard, styles.reversed)}>
            <div className={styles.mockWrapper}>
              <MiniMetrics t={t} />
            </div>
            <div className={styles.problemText}>
              <div className={styles.sectionEyebrow}>{t('problems.vetted.eyebrow')}</div>
              <h3>{t('problems.vetted.title')}</h3>
              <p>{t('problems.vetted.desc')}</p>
              <ul className={styles.benefitItems}>
                {t('problems.vetted.benefits', { returnObjects: true }).map((benefit, i) => (
                  <li key={i}><CheckCircle2 size={18} /> {benefit}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: LATAM STRATEGIC HUB */}
      <section className={styles.latamHubSection}>
        <div className={styles.container}>
          <div className={styles.latamHubHeader}>
            <div className={styles.sectionEyebrow}>{t('latam_hub.eyebrow')}</div>
            <h2 className={styles.sectionTitle}>{t('latam_hub.title')}</h2>
            <p className={styles.darkSectionLead}>
              {t('latam_hub.desc')}
            </p>
          </div>
          
          <div className={styles.latamHubGrid}>
             <div className={styles.latamHubMedia}>
                <img src={IMG_HUB} alt="Strategic Hub" />
                <div className={styles.latamHubOverlay}>
                   <span>{t('latam_hub.innovation.badge')}</span>
                   <h3>{t('latam_hub.innovation.title')}</h3>
                </div>
             </div>
             <div className={styles.latamHubItems}>
                <div className={styles.latamHubItem}>
                   <div className={styles.latamHubIcon}>
                      <Globe size={28} />
                   </div>
                   <div>
                      <h4>{t('latam_hub.items.gateway.title')}</h4>
                      <p>{t('latam_hub.items.gateway.desc')}</p>
                   </div>
                </div>
                <div className={styles.latamHubItem}>
                   <div className={styles.latamHubIcon}>
                      <Landmark size={28} />
                   </div>
                   <div>
                      <h4>{t('latam_hub.items.legacy.title')}</h4>
                      <p>{t('latam_hub.items.legacy.desc')}</p>
                   </div>
                </div>
                <div className={styles.latamHubItem}>
                   <div className={styles.latamHubIcon}>
                      <BarChart3 size={28} />
                   </div>
                   <div>
                      <h4>{t('latam_hub.items.data.title')}</h4>
                      <p>{t('latam_hub.items.data.desc')}</p>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 4. SOLUTIONS GRID */}
      <section className={styles.solutionsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionEyebrow}>{t('solutions_header.eyebrow')}</div>
            <h2 className={styles.sectionTitle}>{t('solutions_header.title')}</h2>
          </div>
          <div className={styles.solutionsGrid}>
            {solutions.map((sol, i) => (
              <div key={i} className={styles.solCard}>
                <div className={styles.solIcon}>{sol.icon}</div>
                <h4>{sol.title}</h4>
                <p>{sol.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: BRAND PRESENCE GALLERY */}
      <section className={styles.brandGallerySection}>
        <div className={styles.container}>
           <div className={styles.brandGalleryGrid}>
              <div className={styles.brandGalleryTile}>
                 <img src={IMG_PRESENCE} alt="Brand Presence" />
                 <div>
                    <h4>{t('gallery.activation')}</h4>
                 </div>
              </div>
              <div className={styles.brandGalleryTile}>
                 <img src={IMG_CONTROL} alt="Metrics Control" />
                 <div>
                    <h4>{t('gallery.control')}</h4>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* 5. TESTIMONIAL */}
      <section className={styles.testimonialSection}>
        <div className={styles.container}>
          <div className={styles.testimonialCard}>
            <div className={styles.quoteIcon}><Quote size={20} /></div>
            <blockquote className={styles.quote}>
              "{t('testimonial.quote')}"
            </blockquote>
            <cite className={styles.cite}>
              <span className={styles.citeName}>{t('testimonial.cite.name')}</span>
              <span className={styles.citeRole}>{t('testimonial.cite.role')}</span>
            </cite>
          </div>
        </div>
      </section>

      {/* 6. FINAL CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaBox}>
            <div className={styles.sectionEyebrow} style={{color: 'rgba(0,0,0,0.5)', borderColor: 'rgba(0,0,0,0.1)'}}>
              {t('cta_final.eyebrow')}
            </div>
            <h2>{t('cta_final.title')}</h2>
            <p>{t('cta_final.desc')}</p>
            <Link to="/register" className={styles.ctaBtn}>
              {t('cta_final.btn')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Brands;


